import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import TextField from "@material-ui/core/TextField";
import Modal from "@material-ui/core/Modal";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Button from "components/CustomButtons/Button.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import AssetTree from "components/AssetTree/AssetTree.jsx";
import AssetStatusChips from "components/AssetStatusChips/AssetStatusChips.jsx";
import {
  buildAssetTree,
  createAsset,
  createAssetType,
  deleteAsset,
  getAssets,
  getAssetTypes,
  updateAsset
} from "services/assetDataProvider";

function toSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function Assets({ axios }) {
  const [assets, setAssets] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [editAssetModalOpen, setEditAssetModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState("");
  const [typeError, setTypeError] = useState("");
  const [editError, setEditError] = useState("");
  const [form, setForm] = useState({
    name: "",
    code: "",
    description: "",
    asset_type: "",
    parent: "",
    active: true
  });
  const [typeForm, setTypeForm] = useState({
    name: "",
    slug: "",
    category: "custom",
    description: ""
  });
  const [editForm, setEditForm] = useState({
    name: "",
    code: "",
    description: "",
    asset_type: "",
    parent: "",
    active: true
  });

  const modalContainerStyle = {
    width: 520,
    maxWidth: "92vw",
    margin: "8vh auto",
    background: "white",
    borderRadius: 12,
    padding: 16,
    display: "grid",
    gap: 10,
    outline: "none",
    borderTop: "4px solid #1d4ed8",
    boxShadow: "0 18px 48px rgba(30, 64, 175, 0.18)"
  };

  const modalTitleStyle = {
    margin: 0,
    color: "#1d4ed8"
  };

  const loadAssets = () => {
    return getAssets(axios).then(data => {
      setAssets(data || []);
      if (data && data.length > 0 && !selectedAssetId) {
        setSelectedAssetId(data[0].id);
      }
    });
  };

  useEffect(() => {
    Promise.all([loadAssets(), getAssetTypes(axios)]).then(([, types]) => {
      setAssetTypes(types || []);
    });
  }, [axios]);

  const visibleAssets = useMemo(() => {
    if (!showActiveOnly) {
      return assets;
    }
    return assets.filter(item => item.active !== false);
  }, [assets, showActiveOnly]);

  const filteredAssetIds = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return new Set(visibleAssets.map(item => item.id));
    }

    const byId = {};
    assets.forEach(item => {
      byId[item.id] = item;
    });

    const matching = visibleAssets.filter(item => {
      const txt = `${item.name || ""} ${item.code || ""}`.toLowerCase();
      return txt.includes(term);
    });

    const keep = new Set();
    matching.forEach(item => {
      keep.add(item.id);
      let cursor = item;
      while (cursor && cursor.parent && byId[cursor.parent]) {
        keep.add(cursor.parent);
        cursor = byId[cursor.parent];
      }
    });

    return keep;
  }, [assets, visibleAssets, searchTerm]);

  const tree = useMemo(() => {
    const filteredAssets = visibleAssets.filter(item => filteredAssetIds.has(item.id));
    return buildAssetTree(filteredAssets);
  }, [visibleAssets, filteredAssetIds]);

  useEffect(() => {
    if (!selectedAssetId) {
      return;
    }
    const existsInVisible = visibleAssets.some(item => item.id === selectedAssetId);
    if (!existsInVisible) {
      setSelectedAssetId("");
    }
  }, [visibleAssets, selectedAssetId]);

  const selectedAsset = useMemo(
    () => assets.find(item => item.id === selectedAssetId),
    [assets, selectedAssetId]
  );

  const assetTypeById = useMemo(() => {
    const lookup = new Map();
    assetTypes.forEach(type => {
      lookup.set(String(type.id), type);
      if (type.slug) {
        lookup.set(String(type.slug), type);
      }
      if (type.name) {
        lookup.set(String(type.name), type);
      }
    });
    return lookup;
  }, [assetTypes]);

  const selectedAssetType = useMemo(() => {
    if (!selectedAsset) {
      return null;
    }

    if (selectedAsset.asset_type && typeof selectedAsset.asset_type === "object") {
      const nestedType = selectedAsset.asset_type;
      const nestedTypeId = nestedType.id == null ? "" : String(nestedType.id);
      const nestedTypeSlug = nestedType.slug == null ? "" : String(nestedType.slug);
      const nestedTypeName = nestedType.name == null ? "" : String(nestedType.name);

      return (
        assetTypeById.get(nestedTypeId) ||
        assetTypeById.get(nestedTypeSlug) ||
        assetTypeById.get(nestedTypeName) ||
        nestedType
      );
    }

    const assetTypeKey = selectedAsset.asset_type == null ? "" : String(selectedAsset.asset_type);
    return assetTypeById.get(assetTypeKey) || null;
  }, [assetTypeById, selectedAsset]);

  const selectedAssetTypeLabel = useMemo(() => {
    if (!selectedAsset) {
      return "-";
    }

    if (selectedAssetType) {
      return selectedAssetType.name || selectedAssetType.label || selectedAssetType.slug || String(selectedAssetType.id || "-");
    }

    if (selectedAsset.asset_type && typeof selectedAsset.asset_type === "object") {
      return selectedAsset.asset_type.name || selectedAsset.asset_type.label || selectedAsset.asset_type.slug || String(selectedAsset.asset_type.id || "-");
    }

    return selectedAsset.asset_type ? String(selectedAsset.asset_type) : "-";
  }, [selectedAsset, selectedAssetType]);

  const openEditModal = () => {
    if (!selectedAsset) {
      return;
    }

    setEditError("");
    setEditForm({
      name: selectedAsset.name || "",
      code: selectedAsset.code || "",
      description: selectedAsset.description || "",
      asset_type: selectedAsset.asset_type || "",
      parent: selectedAsset.parent || "",
      active: selectedAsset.active !== false
    });
    setEditAssetModalOpen(true);
  };

  const openActionsMenu = event => {
    setActionsAnchorEl(event.currentTarget);
  };

  const closeActionsMenu = () => {
    setActionsAnchorEl(null);
  };

  const submit = () => {
    if (!form.name || !form.asset_type) {
      setError("Name and asset type are required.");
      return;
    }

    setSaving(true);
    setError("");
    const payload = {
      name: form.name,
      code: form.code || null,
      description: form.description || null,
      asset_type: form.asset_type,
      active: form.active
    };

    if (form.parent) {
      payload.parent = form.parent;
    }

    createAsset(axios, payload)
      .then(() => {
        setForm({
          name: "",
          code: "",
          description: "",
          asset_type: "",
          parent: "",
          active: true
        });
        setAssetModalOpen(false);
        return loadAssets();
      })
      .catch(err => {
        setError(err.message || "Failed to create asset.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const submitType = () => {
    if (!typeForm.name) {
      setTypeError("Type name is required.");
      return;
    }

    const slug = toSlug(typeForm.slug || typeForm.name);
    if (!slug) {
      setTypeError("Valid slug is required.");
      return;
    }

    setSavingType(true);
    setTypeError("");

    const payload = {
      name: typeForm.name,
      slug,
      category: typeForm.category || "custom",
      description: typeForm.description || null
    };

    createAssetType(axios, payload)
      .then(created => {
        return getAssetTypes(axios).then(types => {
          setAssetTypes(types || []);
          setForm(prev => ({ ...prev, asset_type: created.id }));
          setTypeModalOpen(false);
          setTypeForm({
            name: "",
            slug: "",
            category: "custom",
            description: ""
          });
        });
      })
      .catch(err => {
        setTypeError(err.message || "Failed to create asset type.");
      })
      .finally(() => {
        setSavingType(false);
      });
  };

  const submitEdit = () => {
    if (!selectedAsset) {
      return;
    }

    if (!editForm.name || !editForm.asset_type) {
      setEditError("Name and asset type are required.");
      return;
    }

    setSavingEdit(true);
    setEditError("");

    const payload = {
      name: editForm.name,
      code: editForm.code || null,
      description: editForm.description || null,
      asset_type: editForm.asset_type,
      active: editForm.active
    };

    if (editForm.parent) {
      payload.parent = editForm.parent;
    }

    updateAsset(axios, selectedAsset.id, payload)
      .then(() => loadAssets())
      .then(() => {
        setEditAssetModalOpen(false);
      })
      .catch(err => {
        setEditError(err.message || "Failed to update asset.");
      })
      .finally(() => {
        setSavingEdit(false);
      });
  };

  const removeSelectedAsset = () => {
    if (!selectedAsset) {
      return;
    }
    if (!window.confirm(`Delete asset '${selectedAsset.name}'?`)) {
      return;
    }

    setDeleting(true);
    deleteAsset(axios, selectedAsset.id)
      .then(() => loadAssets())
      .then(() => {
        setSelectedAssetId("");
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const toggleArchiveSelectedAsset = () => {
    if (!selectedAsset) {
      return;
    }

    const targetActive = !selectedAsset.active;
    const actionLabel = targetActive ? "reactivate" : "archive";
    if (!window.confirm(`Do you want to ${actionLabel} '${selectedAsset.name}'?`)) {
      return;
    }

    setArchiving(true);
    updateAsset(axios, selectedAsset.id, { active: targetActive })
      .then(() => loadAssets())
      .finally(() => {
        setArchiving(false);
      });
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={4}>
        <Card>
          <CardHeader color="info">
            <h4>Asset Tree</h4>
          </CardHeader>
          <CardBody>
            <TextField
              fullWidth
              label="Search assets"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showActiveOnly}
                  onChange={e => setShowActiveOnly(e.target.checked)}
                  color="primary"
                />
              }
              label="Show active only"
              style={{ marginBottom: 8 }}
            />
            <AssetTree
              tree={tree}
              selectedAssetId={selectedAssetId}
              onSelect={setSelectedAssetId}
            />
          </CardBody>
        </Card>
      </GridItem>
      <GridItem xs={12} sm={12} md={8}>
        <Card>
          <CardHeader color="info">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8
              }}
            >
              <h4 style={{ margin: 0 }}>Selected Asset</h4>
              <div>
                <IconButton
                  aria-label="asset actions"
                  onClick={openActionsMenu}
                  style={{ color: "white" }}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={actionsAnchorEl}
                  keepMounted
                  open={Boolean(actionsAnchorEl)}
                  onClose={closeActionsMenu}
                >
                  <MenuItem
                    onClick={() => {
                      closeActionsMenu();
                      setTypeModalOpen(true);
                    }}
                  >
                    Create Asset Type
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeActionsMenu();
                      setAssetModalOpen(true);
                    }}
                  >
                    Create Asset
                  </MenuItem>
                  <MenuItem
                    disabled={!selectedAsset}
                    onClick={() => {
                      closeActionsMenu();
                      openEditModal();
                    }}
                  >
                    Edit Asset
                  </MenuItem>
                  <MenuItem
                    disabled={!selectedAsset || archiving}
                    onClick={() => {
                      closeActionsMenu();
                      toggleArchiveSelectedAsset();
                    }}
                  >
                    {!selectedAsset
                      ? "Archive/Reactivate"
                      : selectedAsset.active
                      ? "Archive Asset"
                      : "Reactivate Asset"}
                  </MenuItem>
                  <MenuItem
                    disabled={!selectedAsset || deleting}
                    onClick={() => {
                      closeActionsMenu();
                      removeSelectedAsset();
                    }}
                  >
                    Delete Asset
                  </MenuItem>
                  <MenuItem
                    disabled={!selectedAsset}
                    onClick={() => {
                      closeActionsMenu();
                      if (selectedAsset) {
                        window.location.assign(`/assets/${selectedAsset.id}`);
                      }
                    }}
                  >
                    Open Selected Asset Details
                  </MenuItem>
                </Menu>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {!selectedAsset ? (
              <div style={{ color: "#475569" }}>
                Select an asset from the tree to view details.
              </div>
            ) : (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 14
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <h4 style={{ margin: 0 }}>{selectedAsset.name}</h4>
                    {selectedAsset.active === false ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 10px",
                          borderRadius: 999,
                          background: "#fee2e2",
                          color: "#991b1b",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          textTransform: "uppercase"
                        }}
                      >
                        Archived
                      </span>
                    ) : null}
                  </div>
                  <Button
                    color="info"
                    size="sm"
                    component={Link}
                    to={`/assets/${selectedAsset.id}`}
                    style={{ textDecoration: "none" }}
                  >
                    Open full details
                  </Button>
                </div>
                <div style={{ marginTop: 8, color: "#64748b" }}>
                  {selectedAsset.description || "No description"}
                </div>
                <div style={{ marginTop: 10 }}>
                  <strong>Code:</strong> {selectedAsset.code || "-"}
                </div>
                <div style={{ marginTop: 6 }}>
                  <strong>Type:</strong> {selectedAssetTypeLabel}
                </div>
                <div style={{ marginTop: 6 }}>
                  <strong>Parent:</strong>{" "}
                  {selectedAsset.parent
                    ? (assets.find(item => item.id === selectedAsset.parent) || {}).name || "-"
                    : "None"}
                </div>
                <AssetStatusChips asset={selectedAsset} assetTypeLabel={selectedAssetTypeLabel} />
              </div>
            )}
          </CardBody>
        </Card>
      </GridItem>

      <Modal open={typeModalOpen} onClose={() => setTypeModalOpen(false)}>
        <div style={modalContainerStyle}>
          <h4 style={modalTitleStyle}>Create Asset Type</h4>
          <TextField
            label="Type Name"
            variant="outlined"
            size="small"
            value={typeForm.name}
            onChange={e => {
              const name = e.target.value;
              setTypeForm(prev => ({
                ...prev,
                name,
                slug: prev.slug ? prev.slug : toSlug(name)
              }));
            }}
          />
          <TextField
            label="Type Slug"
            variant="outlined"
            size="small"
            value={typeForm.slug}
            onChange={e => setTypeForm({ ...typeForm, slug: toSlug(e.target.value) })}
          />
          <TextField
            select
            label="Category"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={typeForm.category}
            onChange={e => setTypeForm({ ...typeForm, category: e.target.value })}
          >
            <option value="custom">Custom</option>
            <option value="farm">Farm</option>
            <option value="field">Field</option>
            <option value="zone">Zone</option>
            <option value="storage">Storage</option>
            <option value="infrastructure">Infrastructure</option>
            <option value="sensor_group">Sensor Group</option>
          </TextField>
          <TextField
            label="Type Description"
            variant="outlined"
            size="small"
            value={typeForm.description}
            onChange={e => setTypeForm({ ...typeForm, description: e.target.value })}
          />
          {typeError ? <div style={{ color: "#b91c1c" }}>{typeError}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button color="white" onClick={() => setTypeModalOpen(false)}>
              Cancel
            </Button>
            <Button color="info" onClick={submitType} disabled={savingType}>
              {savingType ? "Creating Type..." : "Create Asset Type"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={assetModalOpen} onClose={() => setAssetModalOpen(false)}>
        <div style={modalContainerStyle}>
          <h4 style={modalTitleStyle}>Create Asset</h4>
          <TextField
            label="Asset Name"
            variant="outlined"
            size="small"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Asset Code"
            variant="outlined"
            size="small"
            value={form.code}
            onChange={e => setForm({ ...form, code: e.target.value })}
          />
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
          />
          <TextField
            select
            label="Asset Type"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={form.asset_type}
            onChange={e => setForm({ ...form, asset_type: e.target.value })}
          >
            <option value="">Select asset type</option>
            {assetTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label="Parent Asset (optional)"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={form.parent}
            onChange={e => setForm({ ...form, parent: e.target.value })}
          >
            <option value="">None</option>
            {assets.map(asset => (
              <option key={asset.id} value={asset.id}>
                {asset.name}
              </option>
            ))}
          </TextField>
          {error ? <div style={{ color: "#b91c1c" }}>{error}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button color="white" onClick={() => setAssetModalOpen(false)}>
              Cancel
            </Button>
            <Button color="info" onClick={submit} disabled={saving}>
              {saving ? "Creating..." : "Create Asset"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal open={editAssetModalOpen} onClose={() => setEditAssetModalOpen(false)}>
        <div style={modalContainerStyle}>
          <h4 style={modalTitleStyle}>Edit Asset</h4>
          <TextField
            label="Asset Name"
            variant="outlined"
            size="small"
            value={editForm.name}
            onChange={e => setEditForm({ ...editForm, name: e.target.value })}
          />
          <TextField
            label="Asset Code"
            variant="outlined"
            size="small"
            value={editForm.code}
            onChange={e => setEditForm({ ...editForm, code: e.target.value })}
          />
          <TextField
            label="Description"
            variant="outlined"
            size="small"
            value={editForm.description}
            onChange={e => setEditForm({ ...editForm, description: e.target.value })}
          />
          <TextField
            select
            label="Asset Type"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={editForm.asset_type}
            onChange={e => setEditForm({ ...editForm, asset_type: e.target.value })}
          >
            <option value="">Select asset type</option>
            {assetTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </TextField>
          <TextField
            select
            label="Parent Asset (optional)"
            variant="outlined"
            size="small"
            SelectProps={{ native: true }}
            value={editForm.parent}
            onChange={e => setEditForm({ ...editForm, parent: e.target.value })}
          >
            <option value="">None</option>
            {assets
              .filter(asset => !selectedAsset || asset.id !== selectedAsset.id)
              .map(asset => (
                <option key={asset.id} value={asset.id}>
                  {asset.name}
                </option>
              ))}
          </TextField>
          {editError ? <div style={{ color: "#b91c1c" }}>{editError}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button color="white" onClick={() => setEditAssetModalOpen(false)}>
              Cancel
            </Button>
            <Button color="info" onClick={submitEdit} disabled={savingEdit}>
              {savingEdit ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </Modal>
    </GridContainer>
  );
}

Assets.propTypes = {
  axios: PropTypes.object.isRequired
};
